/**
 * 经销商端全局状态管理Context
 * 统一管理账号数据、统计信息、用户信息等
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { handleApiRequest, clearCacheByType, CACHE_KEYS } from '../utils/apiUtils';

// 初始状态
const initialState = {
  // 用户信息
  userInfo: {
    id: null,
    username: '',
    fullName: '',
    phone: '',
    role: '',
    location: '未设置地区'
  },
  
  // 账号数据
  accounts: {
    agent: [],
    inspector: [],
    trial: [],
    regular: []
  },
  
  // 统计信息
  stats: {
    agent: { total: 0, active: 0, disabled: 0, remaining: 0 },
    inspector: { total: 0, active: 0, disabled: 0, remaining: 0 },
    trial: { total: 0, active: 0, disabled: 0, remaining: 0 },
    regular: { total: 0, active: 0, disabled: 0, remaining: 0 }
  },
  
  // 分页信息
  pagination: {
    agent: { current: 1, pageSize: 10, total: 0 },
    inspector: { current: 1, pageSize: 10, total: 0 },
    trial: { current: 1, pageSize: 10, total: 0 },
    regular: { current: 1, pageSize: 10, total: 0 }
  },
  
  // 加载状态
  loading: {
    agent: false,
    inspector: false,
    trial: false,
    regular: false,
    stats: false
  },
  
  // 错误信息
  errors: {
    agent: null,
    inspector: null,
    trial: null,
    regular: null
  }
};

// Action类型
const ActionTypes = {
  // 设置用户信息
  SET_USER_INFO: 'SET_USER_INFO',
  
  // 设置账号数据
  SET_ACCOUNTS: 'SET_ACCOUNTS',
  
  // 设置统计信息
  SET_STATS: 'SET_STATS',
  
  // 设置分页信息
  SET_PAGINATION: 'SET_PAGINATION',
  
  // 设置加载状态
  SET_LOADING: 'SET_LOADING',
  
  // 设置错误信息
  SET_ERROR: 'SET_ERROR',
  
  // 清除错误
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // 重置状态
  RESET_STATE: 'RESET_STATE'
};

// Reducer
const dealerReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_USER_INFO:
      return {
        ...state,
        userInfo: { ...state.userInfo, ...action.payload }
      };
      
    case ActionTypes.SET_ACCOUNTS:
      return {
        ...state,
        accounts: {
          ...state.accounts,
          [action.payload.type]: action.payload.data
        }
      };
      
    case ActionTypes.SET_STATS:
      return {
        ...state,
        stats: {
          ...state.stats,
          [action.payload.type]: action.payload.data
        }
      };
      
    case ActionTypes.SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.type]: { ...state.pagination[action.payload.type], ...action.payload.data }
        }
      };
      
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.type]: action.payload.loading
        }
      };
      
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.type]: action.payload.error
        }
      };
      
    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.type]: null
        }
      };
      
    case ActionTypes.RESET_STATE:
      return initialState;
      
    default:
      return state;
  }
};

// Context
const DealerContext = createContext();

// Provider组件
export const DealerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dealerReducer, initialState);
  
  // Action creators
  const actions = {
    // 设置用户信息
    setUserInfo: useCallback((userInfo) => {
      dispatch({
        type: ActionTypes.SET_USER_INFO,
        payload: userInfo
      });
    }, []),
    
    // 设置账号数据
    setAccounts: useCallback((type, data) => {
      dispatch({
        type: ActionTypes.SET_ACCOUNTS,
        payload: { type, data }
      });
    }, []),
    
    // 设置统计信息
    setStats: useCallback((type, data) => {
      dispatch({
        type: ActionTypes.SET_STATS,
        payload: { type, data }
      });
    }, []),
    
    // 设置分页信息
    setPagination: useCallback((type, data) => {
      dispatch({
        type: ActionTypes.SET_PAGINATION,
        payload: { type, data }
      });
    }, []),
    
    // 设置加载状态
    setLoading: useCallback((type, loading) => {
      dispatch({
        type: ActionTypes.SET_LOADING,
        payload: { type, loading }
      });
    }, []),
    
    // 设置错误信息
    setError: useCallback((type, error) => {
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: { type, error }
      });
    }, []),
    
    // 清除错误
    clearError: useCallback((type) => {
      dispatch({
        type: ActionTypes.CLEAR_ERROR,
        payload: { type }
      });
    }, []),
    
    // 重置状态
    resetState: useCallback(() => {
      dispatch({ type: ActionTypes.RESET_STATE });
    }, []),
    
    // 刷新数据（清除缓存）
    refreshData: useCallback((type) => {
      if (type) {
        clearCacheByType(`${CACHE_KEYS.ACCOUNTS}${type}`);
        clearCacheByType(`${CACHE_KEYS.STATS}${type}`);
      } else {
        clearCacheByType(CACHE_KEYS.ACCOUNTS);
        clearCacheByType(CACHE_KEYS.STATS);
      }
    }, [])
  };
  
  return (
    <DealerContext.Provider value={{ state, actions }}>
      {children}
    </DealerContext.Provider>
  );
};

// Hook for using context
export const useDealerContext = () => {
  const context = useContext(DealerContext);
  if (!context) {
    throw new Error('useDealerContext must be used within a DealerProvider');
  }
  return context;
};

// 选择器函数
export const selectors = {
  // 获取用户信息
  getUserInfo: (state) => state.userInfo,
  
  // 获取指定类型的账号数据
  getAccounts: (state, type) => state.accounts[type] || [],
  
  // 获取指定类型的统计信息
  getStats: (state, type) => state.stats[type] || { total: 0, active: 0, disabled: 0, remaining: 0 },
  
  // 获取指定类型的分页信息
  getPagination: (state, type) => state.pagination[type] || { current: 1, pageSize: 10, total: 0 },
  
  // 获取指定类型的加载状态
  getLoading: (state, type) => state.loading[type] || false,
  
  // 获取指定类型的错误信息
  getError: (state, type) => state.errors[type] || null
};

export default DealerContext;
